import { isSignupSendingBlocked } from '@/components/signup/activation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '@/store/store';
import { fetchSequences } from '@/slices/sequencesSlice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
} from '@/components/ui/popover';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { IconButton } from '@/components/ui/custom/IconButton';
import { Workflow, ChevronDown, ExternalLink, Search } from 'lucide-react';
import { BottomSheet, BottomSheetTextButton } from '@/components/mobile';
import type { MessageSequence } from '@/types/messageSequence';
import axios from 'axios';
import { toast } from 'sonner';
import SequenceStepEditor, {
	type EditableStep,
} from '@/components/message-sequence/SequenceStepEditor';
import SequenceStepPreview from '@/components/message-sequence/SequenceStepPreview';
import type { MessageSequenceStep } from '@/types/messageSequence';
import getWebchatApiUrl from '@/utils/getWebchatUrl';

const webchatApiUrl = getWebchatApiUrl(window.location.origin);
// Note: inputs/edit UI live inside SequenceStepEditor

type ActiveConversation =
	| { _id?: string; _customer?: { _id?: string } | string }
	| null
	| undefined;

interface Props {
	activeConversation?: ActiveConversation;
	onSelect?: (sequence: MessageSequence) => void;
	disabled?: boolean;
	/** 'sheet' (phones): no trigger; the caller opens a bottom sheet with open/onOpenChange. */
	variant?: 'popover' | 'sheet';
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

const AddToSequenceButton: React.FC<Props> = ({
	activeConversation,
	onSelect,
	disabled: disabledProp = false,
	variant = 'popover',
	open: openProp,
	onOpenChange,
}) => {
	const sendingBlocked = useSelector((state: RootState) => isSignupSendingBlocked(state.auth));
	const disabled = disabledProp || sendingBlocked;
	const blockedRef = useRef(disabled);
	blockedRef.current = disabled;
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const { items, loading, lastFetched } = useSelector(
		(s: RootState) => s.sequences
	);
	const [openState, setOpenState] = useState(false);
	const open = openProp ?? openState;
	const setOpen = (next: boolean) => {
		if (openProp === undefined) setOpenState(next);
		onOpenChange?.(next);
	};
	const [subscribingId, setSubscribingId] = useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [pendingSeq, setPendingSeq] = useState<MessageSequence | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [editedSteps, setEditedSteps] = useState<
		{ text: string; delayDays: number; atTime: string }[]
	>([]);
	const [query, setQuery] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

	useEffect(() => {
		// Only fetch once when opening and we have never fetched in this session
		if (!open) return;
		if (loading) return;
		if (items.length > 0) return;
		if (lastFetched !== null) return;
		dispatch(fetchSequences());
	}, [open, loading, items.length, lastFetched, dispatch]);

	const sorted = useMemo(() => {
		return [...items].sort((a, b) =>
			(b.createdAt || '').localeCompare(a.createdAt || '')
		);
	}, [items]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return sorted;
		return sorted.filter((seq) =>
			(seq.title || '').toString().toLowerCase().includes(q)
		);
	}, [query, sorted]);

	const formatStepTime = (step: MessageSequenceStep) => {
		const trim = (s?: string) => (typeof s === 'string' ? s.trim() : '');
		const dNow = new Date();
		const atLocal = trim(step.atTime);
		if (atLocal) {
			const [hStr, mStr] = atLocal.split(':');
			const hours = Number(hStr);
			const minutes = Number(mStr ?? '0');
			const d = new Date(dNow);
			d.setHours(
				Number.isFinite(hours) ? hours : 0,
				Number.isFinite(minutes) ? minutes : 0,
				0,
				0
			);
			return d.toLocaleTimeString([], {
				hour: 'numeric',
				minute: '2-digit',
			});
		}
		const atUtc = trim(step.atTimeUtc);
		if (atUtc) {
			const [hStr, mStr] = atUtc.split(':');
			const hours = Number(hStr);
			const minutes = Number(mStr ?? '0');
			const d = new Date(
				Date.UTC(
					dNow.getUTCFullYear(),
					dNow.getUTCMonth(),
					dNow.getUTCDate(),
					Number.isFinite(hours) ? hours : 0,
					Number.isFinite(minutes) ? minutes : 0,
					0,
					0
				)
			);
			return d.toLocaleTimeString([], {
				hour: 'numeric',
				minute: '2-digit',
			});
		}
		if (typeof step.atTimeUtcMinutes === 'number') {
			const hours = Math.floor(step.atTimeUtcMinutes / 60);
			const minutes = step.atTimeUtcMinutes % 60;
			const d = new Date(
				Date.UTC(
					dNow.getUTCFullYear(),
					dNow.getUTCMonth(),
					dNow.getUTCDate(),
					hours,
					minutes,
					0,
					0
				)
			);
			return d.toLocaleTimeString([], {
				hour: 'numeric',
				minute: '2-digit',
			});
		}
		return 'time not set';
	};

	const openConfirmForSequence = (seq: MessageSequence) => {
		if (blockedRef.current) return;
		const customerId =
			(activeConversation &&
				(typeof activeConversation._customer === 'string'
					? activeConversation._customer
					: activeConversation._customer &&
					  (activeConversation._customer as { _id?: string })
							._id)) ||
			undefined;

		if (!customerId) {
			toast.error('No customer selected', {
				description: 'Select a conversation/customer first.',
			});
			return;
		}

		setPendingSeq(seq);
		setConfirmOpen(true);
	};

	useEffect(() => {
		// Reset search and highlight when popover closes
		if (!open) {
			setQuery('');
			setHighlightedIndex(-1);
		}
	}, [open]);

	useEffect(() => {
		if (highlightedIndex < 0) return;
		const el = document.getElementById(
			`add-seq-option-${highlightedIndex}`
		);
		if (el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'nearest' });
		}
	}, [highlightedIndex]);

	const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
		e
	) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (filtered.length === 0) return;
			setHighlightedIndex((prev) => {
				if (prev < 0) return 0;
				return Math.min(prev + 1, filtered.length - 1);
			});
			return;
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (filtered.length === 0) return;
			setHighlightedIndex((prev) => {
				if (prev <= 0) return 0;
				return prev - 1;
			});
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
				openConfirmForSequence(filtered[highlightedIndex]);
			}
		}
	};

	const utcMinutesToLocalHhmm = (utcMinutes: number): string => {
		const tzOffset = new Date().getTimezoneOffset();
		const localMinutes = (((utcMinutes - tzOffset) % 1440) + 1440) % 1440;
		const h = Math.floor(localMinutes / 60);
		const m = localMinutes % 60;
		const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
		return `${pad(h)}:${pad(m)}`;
	};

	const initializeEditedSteps = (seq: MessageSequence) => {
		const steps = Array.isArray(seq.steps) ? seq.steps : [];
		const mapped = steps.map((s, idx) => {
			const at =
				s.atTime && s.atTime.trim()
					? s.atTime
					: typeof s.atTimeUtcMinutes === 'number'
					? utcMinutesToLocalHhmm(Number(s.atTimeUtcMinutes))
					: '09:00';
			return {
				text: s.text || '',
				delayDays: Number(s.delayDays || 0),
				atTime: at,
				// Only the first step may send immediately
				sendImmediately: idx === 0 ? !!s.sendImmediately : false,
			} as EditableStep;
		});
		setEditedSteps(mapped);
	};

	const handleSelect = async (seq: MessageSequence) => {
		if (blockedRef.current) return;
		const customerId =
			(activeConversation &&
				(typeof activeConversation._customer === 'string'
					? activeConversation._customer
					: activeConversation._customer &&
					  (activeConversation._customer as { _id?: string })
							._id)) ||
			undefined;

		if (!customerId) {
			toast.error('No customer selected', {
				description: 'Select a conversation/customer first.',
			});
			return;
		}

		try {
			setSubscribingId(seq._id || null);
			await axios.post(
				`${webchatApiUrl}/api/message-sequences/instances`,
				{
					_messageSequence: seq._id,
					_customer: customerId,
				}
			);
			toast.success('Customer added to sequence', {
				description: seq.title || 'Sequence',
			});
			// Notify views to refresh instances for this customer
			try {
				window.dispatchEvent(
					new CustomEvent('message-sequences:refresh', {
						detail: { _customer: customerId },
					})
				);
			} catch {
				// no-op
			}
			if (onSelect) onSelect(seq);
			setOpen(false);
		} catch (err) {
			const e = err as unknown as {
				response?: { data?: { message?: string } };
				message?: string;
			};
			const msg =
				e?.response?.data?.message ||
				e?.message ||
				'Failed to subscribe customer';
			toast.error('Could not add to sequence', { description: msg });
		} finally {
			setSubscribingId(null);
		}
	};

	const handleCreateCustomAndSelect = async () => {
		if (blockedRef.current) return;
		const customerId =
			(activeConversation &&
				(typeof activeConversation._customer === 'string'
					? activeConversation._customer
					: activeConversation._customer &&
					  (activeConversation._customer as { _id?: string })
							._id)) ||
			undefined;

		if (!customerId || !pendingSeq) {
			toast.error('No customer selected', {
				description: 'Select a conversation/customer first.',
			});
			return;
		}

		try {
			setSubscribingId('custom');
			const payload = {
				title: pendingSeq.title || 'Custom sequence',
				isContinueToSendAfterCustomerResponds:
					!!pendingSeq.isContinueToSendAfterCustomerResponds,
				steps: editedSteps,
				_customer: customerId,
			};
			const { data: created } = await axios.post(
				`${webchatApiUrl}/api/message-sequences/create-custom`,
				payload,
				{
					headers: {
						'x-tz-offset-minutes': String(
							new Date().getTimezoneOffset()
						),
					},
				}
			);

			if (blockedRef.current) return;
			await axios.post(
				`${webchatApiUrl}/api/message-sequences/instances`,
				{
					_messageSequence: created._id,
					_customer: customerId,
				}
			);

			// Ensure global sequences list includes the newly created custom sequence
			try {
				await dispatch(fetchSequences());
			} catch (_e) {
				void _e;
			}

			toast.success('Customer added to custom sequence', {
				description: created.title || 'Sequence',
			});
			// Notify views to refresh instances for this customer
			try {
				window.dispatchEvent(
					new CustomEvent('message-sequences:refresh', {
						detail: { _customer: customerId },
					})
				);
			} catch {
				// no-op
			}
			if (onSelect) onSelect(created as MessageSequence);
			setOpen(false);
		} catch (err) {
			const e = err as unknown as {
				response?: { data?: { message?: string } };
				message?: string;
			};
			const msg =
				e?.response?.data?.message ||
				e?.message ||
				'Failed to create custom sequence';
			toast.error('Could not add to sequence', { description: msg });
		} finally {
			setSubscribingId(null);
		}
	};

	const sequenceSheet =
		variant === 'sheet' ? (
			<BottomSheet
				open={open}
				onOpenChange={setOpen}
				title="Add to sequence"
				size="large"
				headerLeft={
					<BottomSheetTextButton onClick={() => setOpen(false)}>
						Cancel
					</BottomSheetTextButton>
				}
				headerRight={
					<BottomSheetTextButton
						onClick={() => {
							setOpen(false);
							navigate('/sequences');
						}}
					>
						Sequences
					</BottomSheetTextButton>
				}
			>
				{sorted.length === 0 && !loading ? (
					<div className="py-8 text-center">
						<p className="text-[17px] font-medium">No sequences yet</p>
						<p className="mt-1 text-[15px] text-muted-foreground">
							Automate sending sequences of messages to your customers.
						</p>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<label className="flex h-11 items-center gap-2 rounded-[10px] bg-muted px-3">
							<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
							<input
								type="search"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search sequences"
								aria-label="Search sequences"
								className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
								autoComplete="off"
								autoCapitalize="none"
								enterKeyHint="search"
							/>
						</label>
						{filtered.length === 0 ? (
							<p className="py-6 text-center text-[15px] text-muted-foreground">
								No matching sequences
							</p>
						) : (
							<div
								className="overflow-hidden rounded-xl border border-border/60"
								role="listbox"
								aria-label="Sequences"
							>
								{filtered.map((seq, index) => (
									<button
										key={seq._id}
										type="button"
										role="option"
										aria-selected={false}
										onClick={() => openConfirmForSequence(seq)}
										disabled={disabled || subscribingId === seq._id}
										className={`flex min-h-14 w-full flex-col justify-center px-4 py-2.5 text-left active:bg-muted disabled:opacity-40 ${
											index > 0 ? 'border-t border-border/60' : ''
										}`}
									>
										<span className="truncate text-[17px] leading-snug">
											{seq.title || 'Untitled'}
										</span>
										<span className="text-[13px] text-muted-foreground">
											{(seq.steps || []).length} message
											{(seq.steps || []).length === 1 ? '' : 's'}
										</span>
									</button>
								))}
							</div>
						)}
					</div>
				)}
			</BottomSheet>
		) : null;

	return (
		<>
			{sequenceSheet}
			{variant !== 'sheet' && (
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<span>
						<IconButton
							aria-label="add to sequence"
							disabled={disabled}
							toolTipTitle="Add to sequence"
						>
							<Workflow className="size-4" />
							<span className="sr-only">Add to sequence</span>
						</IconButton>
					</span>
				</PopoverTrigger>
				<PopoverContent
					align="start"
					className="w-80 p-2 overflow-x-hidden"
				>
					{sorted.length === 0 && !loading ? (
						<div className="px-2 py-3">
							<div className="rounded-md border bg-muted/40 p-3">
								<div className="flex items-start gap-3">
									<Workflow
										className="size-5 text-muted-foreground mt-0.5"
										aria-hidden="true"
									/>
									<div className="min-w-0">
										<p className="text-sm font-medium">
											No sequences yet
										</p>
										<p className="text-xs text-muted-foreground mt-1">
											Automate sending sequences of
											messages to your customers.
										</p>
										<a
											href="/sequences"
											className="inline-flex items-center mt-2 text-xs font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
											aria-label="Go to sequences"
										>
											Go to Sequences
										</a>
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-1">
								<Input
									value={query}
									onChange={(e) =>
										setQuery(e.target.value)
									}
									onKeyDown={handleInputKeyDown}
									placeholder="Search sequences..."
									className="h-8 text-sm"
								/>
								<IconButton
									aria-label="go to sequences"
									toolTipTitle="Go to Sequences"
									className="size-8 shrink-0"
									onClick={() => {
										setOpen(false);
										navigate('/sequences');
									}}
								>
									<ExternalLink className="size-3.5" />
								</IconButton>
							</div>
							{filtered.length === 0 ? (
								<p className="text-sm text-muted-foreground px-1 py-1">
									No matching sequences
								</p>
							) : (
								<div
									className="flex flex-col gap-1 max-h-72 overflow-y-auto overflow-x-hidden"
									role="listbox"
									aria-label="Sequences"
								>
									{filtered.map((seq, index) => (
										<Button
											key={seq._id}
											id={`add-seq-option-${index}`}
											variant="ghost"
											className={`justify-start h-auto py-2 px-2 w-full ${
												highlightedIndex === index
													? 'bg-accent'
													: ''
											}`}
											onMouseEnter={() =>
												setHighlightedIndex(index)
											}
											onClick={() =>
												openConfirmForSequence(seq)
											}
											disabled={disabled || subscribingId === seq._id}
											role="option"
											aria-selected={
												highlightedIndex === index
											}
										>
											<div className="flex flex-col text-left min-w-0 overflow-hidden">
												<span className="text-sm font-semibold leading-tight truncate">
													{seq.title || 'Untitled'}
												</span>
												<span className="text-xs text-muted-foreground">
													{(seq.steps || []).length}{' '}
													message
													{(seq.steps || [])
														.length === 1
														? ''
														: 's'}
												</span>
											</div>
										</Button>
									))}
								</div>
							)}
						</div>
					)}
				</PopoverContent>
			</Popover>
			)}
			<Dialog
				open={confirmOpen}
				onOpenChange={(v) => {
					setConfirmOpen(v);
					if (!v) setPendingSeq(null);
				}}
			>
				<DialogContent className="sm:max-w-3xl" mobileVariant="fullscreen">
					<DialogHeader>
						<DialogTitle>
							Add to &apos;
							{pendingSeq?.title || 'Untitled'}&apos; sequence
						</DialogTitle>
						<DialogDescription>
							You can edit the sequence before adding the
							customer, and cancel it if needed from the side
							menu.
						</DialogDescription>
					</DialogHeader>
					<div className="mt-2 border rounded-md p-3 max-h-[70vh] overflow-y-auto">
						{pendingSeq?.steps && pendingSeq.steps.length > 0 ? (
							<ul className="space-y-2">
								{pendingSeq.steps.map((step, idx) => (
									<React.Fragment key={idx}>
										{!isEditing ? (
											<SequenceStepPreview
												step={step}
												index={idx}
												formatStepTime={formatStepTime}
												isLast={
													idx ===
													(pendingSeq.steps?.length ||
														0) -
														1
												}
											/>
										) : (
											<SequenceStepEditor
												index={idx}
												step={
													editedSteps[
														idx
													] as EditableStep
												}
												onChange={(next) => {
													const arr = [
														...editedSteps,
													];
													arr[idx] = next;
													setEditedSteps(arr);
												}}
											/>
										)}
										{!isEditing &&
										idx <
											(pendingSeq.steps?.length || 0) -
												1 ? (
											<div className="flex items-center gap-2 py-2">
												<div className="h-px flex-1 bg-border" />
												<div className="rounded-full bg-muted p-1 text-muted-foreground">
													<ChevronDown className="h-4 w-4" />
												</div>
												<div className="h-px flex-1 bg-border" />
											</div>
										) : null}
									</React.Fragment>
								))}
							</ul>
						) : (
							<p className="text-sm text-muted-foreground">
								No steps in this sequence.
							</p>
						)}
					</div>
					<div className="-mt-1">
						{pendingSeq ? (
							<p className="text-xs text-muted-foreground">
								{pendingSeq.isContinueToSendAfterCustomerResponds
									? 'The sequence will continue even if the customer replies.'
									: 'The sequence will stop if the customer replies.'}
							</p>
						) : null}
					</div>
					<div>
						<Button
							variant={isEditing ? 'default' : 'outline'}
							onClick={() => {
								if (!isEditing && pendingSeq) {
									initializeEditedSteps(pendingSeq);
								}
								setIsEditing((v) => !v);
							}}
						>
							{isEditing ? 'Done editing' : 'Edit sequence'}
						</Button>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setConfirmOpen(false)}
							disabled={subscribingId === pendingSeq?._id}
						>
							Cancel
						</Button>
						<Button
							onClick={() => {
								if (!pendingSeq) return;
								setConfirmOpen(false);
								if (isEditing) {
									handleCreateCustomAndSelect();
								} else {
									handleSelect(pendingSeq);
								}
							}}
							disabled={disabled || !pendingSeq}
						>
							{subscribingId === pendingSeq?._id
								? 'Adding…'
								: 'Add to sequence'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default AddToSequenceButton;
